// Cutting text that carries `[[…]]` reference tokens.
//
// Model-authored text is full of them — `[[p3]]`, `[[v7]]`, `[[m2]]`, `[[c5]]`,
// `[[u1]]`, and the version alias `[[p3/4]]`. Each becomes an interactive chip.
// A raw index cut that lands inside one leaves `[[p1`, which no tokenizer can
// claim: it renders as literal bracket junk, and when the cut happened at write
// time the junk is PERSISTED and no re-render can undo it.
//
// So a `[[…]]` TOKEN IS ATOMIC. It is taken whole or dropped whole — never
// split, at any limit, for any reason. Everything here exists to hold that
// line, and it lives in `shared` rather than beside a renderer because the cuts
// that do irreversible damage are the ones on the write path.
//
// Two layers:
//   - `safeCutEnd` / `safeCutStart` — index adjusters. Any cutter that has its
//     own marker, budget arithmetic or word-boundary rule keeps all of that and
//     just asks where it is ALLOWED to cut.
//   - `truncateMarkdownSource` — the full treatment for rendering markdown
//     source: the safe cut, an ellipsis, and emphasis runs re-closed.

import {graphemeClusterEnd, graphemeSafeEnd, graphemeSafeStart} from './unicode.js';

const ELLIPSIS = '..';
const REFERENCE_OPENER = '[[';
const REFERENCE_CLOSER = ']]';

// Longest first: `**` must be matched before the `*` branch sees its asterisks,
// and `~~` before anything scans its tildes. `_` is deliberately absent —
// intra-word underscores (snake_case identifiers) are literal in markdown, so
// balancing them would mangle ordinary text.
const EMPHASIS_MARKERS = ['~~', '**', '`', '*'] as const;

const CODE_MARKER = '`';

interface SafeCutInput {
    source: string;
    cut:    number;
}

interface ReferenceSafeCut {
    cut: number;
    // End of the token the cut landed inside, when that token is well-formed —
    // the only cut allowed to exceed the limit rather than split it.
    wholeTokenEnd: number | null;
}

// Move `cut` off the middle of a `[[…]]` token, towards the token's OPENER. The
// token is atomic, so the cut goes to one of its two edges and never between
// them. An unterminated `[[` has no closer to reach and is dropped at the
// opener whatever that costs — a bare opener on screen is the one outcome this
// module exists to prevent.
function referenceSafeCutEnd(input: SafeCutInput): ReferenceSafeCut {
    const {source, cut} = input;
    const opener = source.lastIndexOf(REFERENCE_OPENER, cut);
    if (opener < 0) {
        return {cut, wholeTokenEnd: null};
    }

    const closer = source.indexOf(REFERENCE_CLOSER, opener);
    const tokenEnd = closer < 0 ? -1 : closer + REFERENCE_CLOSER.length;
    if (tokenEnd >= 0 && tokenEnd <= cut) {
        return {cut, wholeTokenEnd: null};
    }

    return {cut: opener, wholeTokenEnd: tokenEnd < 0 ? null : tokenEnd};
}

// The largest index at or before `cut` that splits neither a `[[…]]` token nor
// a grapheme cluster. Returns 0 when nothing before `cut` can be kept safely —
// callers that must render something decide what to do with that.
export function safeCutEnd(input: SafeCutInput): number {
    const {source, cut} = input;
    if (cut <= 0) return 0;
    if (cut >= source.length) return source.length;

    const reference = referenceSafeCutEnd({source, cut});
    return graphemeSafeEnd({text: source, index: reference.cut});
}

// The mirror for a window that keeps a string's TAIL: the smallest index at or
// after `cut` that splits neither a token nor a cluster. A left cut lands on
// the same tokens from the other side — `p12]]` is junk exactly as `[[p1` is —
// so here the cut moves FORWARD past the whole token instead of back.
export function safeCutStart(input: SafeCutInput): number {
    const {source, cut} = input;
    if (cut <= 0) return 0;
    if (cut >= source.length) return source.length;

    const opener = source.lastIndexOf(REFERENCE_OPENER, cut);
    if (opener >= 0 && opener < cut) {
        const closer = source.indexOf(REFERENCE_CLOSER, opener);
        const tokenEnd = closer < 0 ? -1 : closer + REFERENCE_CLOSER.length;
        // The cut sits inside this token; step past its closer rather than
        // keeping a headless tail of it.
        if (tokenEnd > cut) return graphemeSafeStart({text: source, index: tokenEnd});
    }

    return graphemeSafeStart({text: source, index: cut});
}

// Emphasis/code markers left open by the slice, innermost last. Markers inside
// a code span are literal, so an open backtick suppresses the emphasis scan
// until it closes.
function unclosedMarkers(kept: string): string[] {
    const open: string[] = [];
    let index = 0;

    while (index < kept.length) {
        if (open[open.length - 1] === CODE_MARKER) {
            if (kept[index] === CODE_MARKER) {
                open.pop();
            }
            index += 1;
            continue;
        }

        const marker = EMPHASIS_MARKERS.find((candidate) => kept.startsWith(candidate, index));
        if (marker == null) {
            index += 1;
            continue;
        }

        const openedAt = open.lastIndexOf(marker);
        if (openedAt >= 0) {
            open.splice(openedAt, 1);
        } else {
            open.push(marker);
        }
        index += marker.length;
    }

    return open;
}

// Closers for every run the cut left open, innermost first.
function closingMarkers(kept: string): string {
    return unclosedMarkers(kept).reverse().join('');
}

// Character truncation safe to apply to markdown SOURCE.
//
// Beyond the atomic-reference and grapheme rules, emphasis is handled the other
// way round from a token: pulling the cut back to before an unclosed `**` used
// to discard everything the run covered — a title bolded end to end truncated
// to the EMPTY STRING, so a reference chip rendered its icon and no name at
// all. A run the cut lands inside is RE-CLOSED after the ellipsis instead:
// `a **bold te..**`. Nothing is discarded and the source still parses.
//
// A non-empty source under a positive limit yields non-empty output, EXCEPT
// where the atomic-reference rule forbids it. Kept text may exceed the limit in
// exactly two cases — an outsized grapheme cluster and a whole reference token
// kept rather than split — because an indivisible unit cannot be divided to fit.
export function truncateMarkdownSource(input: {source: string; limit: number}): string {
    const {source, limit} = input;
    if (limit <= 0 || source.length <= limit) {
        return source;
    }

    const reference = referenceSafeCutEnd({source, cut: limit});
    // Dropping the token emptied the result, so keep it whole instead — an
    // atomic unit that does not fit is taken entire, never halved. With no
    // closer to reach there is nothing whole to keep, and empty is correct.
    if (reference.cut === 0) {
        return reference.wholeTokenEnd == null
            ? ''
            : `${source.slice(0, reference.wholeTokenEnd)}${ELLIPSIS}`;
    }

    const graphemeCut = graphemeSafeEnd({text: source, index: reference.cut});
    // The limit can land inside the very first cluster — a two-code-unit emoji
    // under a limit of one. A cluster is indivisible, so the honest minimum is
    // the whole first one, even though it overshoots the limit.
    const cut = graphemeCut > 0 ? graphemeCut : graphemeClusterEnd({text: source, index: 0});
    const kept = source.slice(0, cut).replace(/\s+$/, '');
    if (kept.length === 0) {
        return kept;
    }

    return `${kept}${ELLIPSIS}${closingMarkers(kept)}`;
}
